import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AdminLoginPage = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (isAuthenticated) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 pt-28 pb-24"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <p className="text-[#C9A962] text-xs tracking-[0.3em] mb-3">SECURE ACCESS</p>
          <h1 className="font-display text-4xl text-white mb-2">Admin Login</h1>
          <p className="text-[#666] text-sm">
            Authorized personnel only. All access is logged.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#111] border border-[#222] rounded p-8 space-y-6"
        >
          <div>
            <Label className="text-xs text-[#666] tracking-wider mb-2 block">EMAIL</Label>
            <div className="relative">
              <Mail
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@campcormier.com"
                required
                className="premium-input rounded py-3 pl-10 pr-4"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#666] tracking-wider mb-2 block">PASSWORD</Label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]"
              />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="premium-input rounded py-3 pl-10 pr-4"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <p
              className="text-red-400 text-xs text-center"
              data-testid="login-error-message"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="btn-premium w-full py-3 text-xs rounded"
            data-testid="login-submit-btn"
          >
            {submitting ? "AUTHENTICATING..." : "SIGN IN"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
